import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getArticleGroupDiscountById,
  putArticleGroupDiscount,
} from "../../app/services/articleGroupDiscountService";
import { useAppDispatch } from "../../app/hooks";
import { useAccount, useMsal } from "@azure/msal-react";
import Form from "react-bootstrap/Form";
import { Alert, Breadcrumb, Button, Col, Row } from "react-bootstrap";
import {
  ArticleGroupDiscount,
  defaultArticleGroupDiscount,
} from "../../models/responses/articleGroupDiscount";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";

interface Props {}

const ArticleGroupDiscountEdit: FC<Props> = () => {
  const { articleGroupDiscountId } = useParams<{
    articleGroupDiscountId: string;
  }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [displayError, setDisplayError] = useState(false);
  const isWaitingArticleGroupDiscountPutResponse = useSelector(
    (state: RootState) =>
      state.articleGroupDiscount.isWaitingArticleGroupDiscountPutResponse,
  );
  const articleGroupDiscount = useSelector(
    (state: RootState) => state.articleGroupDiscount.articleGroupDiscount,
  );
  const [articleGroupDiscountData, setArticleGroupDiscountData] =
    useState<ArticleGroupDiscount>(defaultArticleGroupDiscount);

  useEffect(() => {
    dispatch(
      getArticleGroupDiscountById(instance, account, articleGroupDiscountId),
    );
  }, [account, accounts, dispatch, instance, articleGroupDiscountId]);
  useEffect(() => {
    if (articleGroupDiscount) {
      setArticleGroupDiscountData({
        id: articleGroupDiscount.id,
        name: articleGroupDiscount.name,
        description: articleGroupDiscount.description,
        externalGroupId: articleGroupDiscount.externalGroupId,
        discountPercentage: articleGroupDiscount.discountPercentage,
        createdAt: articleGroupDiscount.createdAt,
        updatedAt: articleGroupDiscount.updatedAt,
      });
    }
  }, [articleGroupDiscount]);

  const handleUpdateArticleGroupDiscount = () => {
    setDisplayError(false);
    dispatch(
      putArticleGroupDiscount(
        instance,
        account,
        articleGroupDiscountData,
        onSuccess,
      ),
    );
  };

  const onSuccess = (articleGroupDiscountId: string | null) => {
    if (articleGroupDiscountId === null) {
      setDisplayError(true);
      return;
    }

    setTimeout(() => {
      navigate(`../../${articleGroupDiscountId}`, { relative: "path" });
    }, 500);
  };

  return (
    <div>
      <Breadcrumb>
        <Breadcrumb.Item href="/article-group-discounts">
          Artikelgruppsrabatter
        </Breadcrumb.Item>
        <Breadcrumb.Item active>Uppdatera artikelgruppsrabatt</Breadcrumb.Item>
      </Breadcrumb>
      <hr />
      <Form className="w-3">
        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Namn *</Form.Label>
              <Form.Control
                type="text"
                value={articleGroupDiscountData.name}
                onInput={(e) =>
                  setArticleGroupDiscountData({
                    ...articleGroupDiscountData,
                    name: e.currentTarget.value,
                  })
                }
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Beskrivning *</Form.Label>
              <Form.Control
                type="text"
                value={articleGroupDiscountData.description}
                onInput={(e) =>
                  setArticleGroupDiscountData({
                    ...articleGroupDiscountData,
                    description: e.currentTarget.value,
                  })
                }
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Rabatt %</Form.Label>
              <Form.Control
                type="number"
                value={articleGroupDiscountData.discountPercentage}
                onInput={(e) =>
                  setArticleGroupDiscountData({
                    ...articleGroupDiscountData,
                    discountPercentage: Number(e.currentTarget.value),
                  })
                }
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Artikelgrupp (unikt ID)</Form.Label>
              <Form.Control
                type="text"
                value={articleGroupDiscountData.externalGroupId}
                onInput={(e) =>
                  setArticleGroupDiscountData({
                    ...articleGroupDiscountData,
                    externalGroupId: e.currentTarget.value,
                  })
                }
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="text-end">
          <Button
            variant="primary"
            className="mb-3"
            onClick={handleUpdateArticleGroupDiscount}
            disabled={
              !(
                articleGroupDiscountData.name !== undefined &&
                articleGroupDiscountData.name.length > 0 &&
                articleGroupDiscountData.description !== undefined &&
                articleGroupDiscountData.description.length > 0 &&
                articleGroupDiscountData.discountPercentage !== undefined &&
                articleGroupDiscountData.discountPercentage >= 0 &&
                !isWaitingArticleGroupDiscountPutResponse
              )
            }
          >
            Uppdatera
          </Button>
        </div>

        <Alert key="alert" variant="danger" show={displayError}>
          <p>Något gick fel, kontrollera uppgifterna och försök igen.</p>
        </Alert>
      </Form>
    </div>
  );
};

export default ArticleGroupDiscountEdit;
